import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiTriggerContactRfqs,
  apiPollContactRfqs,
  type MergedQuote,
  type ContactRfqsStatus,
} from 'api/quote-marketplace';
import { formatDateToString } from 'utils/helpers';

const POLL_INTERVAL = 1500;

const getDefaultDateRange = (): { from: string; to: string } => {
  const today = new Date();
  const from = new Date(today);
  from.setMonth(from.getMonth() - 1);
  const to = new Date(today);
  to.setMonth(to.getMonth() + 2);

  return {
    from: formatDateToString(from)!,
    to: formatDateToString(to)!,
  };
};

export interface DateRange {
  from: string;
  to: string;
}

interface UseContactRfqsOptions {
  enabled?: boolean;
}

interface UseContactRfqsResult {
  quotes: MergedQuote[];
  count: number;
  status: ContactRfqsStatus;
  error: string | null;
  isLoading: boolean;
  search: (dateRange: DateRange) => void;
  activeDateRange: DateRange;
}

export const useContactRfqs = (
  contactRef: string | undefined,
  { enabled = true }: UseContactRfqsOptions = {},
): UseContactRfqsResult => {
  const queryClient = useQueryClient();
  const [isPolling, setIsPolling] = useState(false);
  const hasTriggeredRef = useRef(false);
  const [activeDateRange, setActiveDateRange] =
    useState<DateRange>(getDefaultDateRange);

  const triggerMutation = useMutation({
    mutationFn: ({ ref, dateRange }: { ref: string; dateRange: DateRange }) =>
      apiTriggerContactRfqs(ref, {
        effectiveDateFrom: dateRange.from,
        effectiveDateTo: dateRange.to,
      }),
    onSuccess: (_data, variables) => {
      setActiveDateRange(variables.dateRange);
      setIsPolling(true);
    },
  });

  const pollQuery = useQuery({
    queryKey: ['contact-rfqs-poll', contactRef],
    queryFn: () => apiPollContactRfqs(contactRef!),
    enabled: enabled && !!contactRef && (isPolling || hasTriggeredRef.current),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'pending') {
        return POLL_INTERVAL;
      }
      return false;
    },
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (pollQuery.data?.status && pollQuery.data.status !== 'pending') {
      setIsPolling(false);
    }
  }, [pollQuery.data?.status]);

  const { mutate: triggerMutate } = triggerMutation;

  useEffect(() => {
    if (enabled && contactRef && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      const defaultRange = getDefaultDateRange();
      triggerMutate({ ref: contactRef, dateRange: defaultRange });
    }
  }, [enabled, contactRef, triggerMutate]);

  useEffect(() => {
    hasTriggeredRef.current = false;
    setIsPolling(false);
    setActiveDateRange(getDefaultDateRange());
    queryClient.removeQueries({ queryKey: ['contact-rfqs-poll'] });
  }, [contactRef, queryClient]);

  const search = useCallback(
    (dateRange: DateRange) => {
      if (!contactRef) return;

      queryClient.removeQueries({ queryKey: ['contact-rfqs-poll'] });
      setIsPolling(false);

      setTimeout(() => {
        triggerMutate({ ref: contactRef, dateRange });
      }, 0);
    },
    [contactRef, triggerMutate, queryClient],
  );

  const data = pollQuery.data;
  const isLoading =
    triggerMutation.isPending || isPolling || pollQuery.isFetching;

  const status: ContactRfqsStatus =
    isPolling && !data?.status ? 'pending' : data?.status || 'idle';

  return {
    quotes: data?.quotes || [],
    count: data?.count || 0,
    status,
    error: data?.error || null,
    isLoading,
    search,
    activeDateRange,
  };
};
